import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UsageEventFilters } from '../api/usage';
import { EventsPanel, PricesPanel, SummaryPanel, UsersPanel } from '../components/UsagePanels';
import { addDays, kstDate, kstPeriod } from '../utils/usage';

type Tab = 'summary' | 'users' | 'events' | 'prices';
type Preset = 'today' | '7d' | '30d' | 'thisMonth' | 'lastMonth';

const presetRange = (preset: Preset): [string, string] => {
  const today = kstDate();
  const monthStart = `${today.slice(0, 8)}01`;
  switch (preset) {
    case 'today':
      return [today, today];
    case '7d':
      return [addDays(today, -6), today];
    case '30d':
      return [addDays(today, -29), today];
    case 'thisMonth':
      return [monthStart, today];
    case 'lastMonth': {
      const lastMonthEnd = addDays(monthStart, -1);
      return [`${lastMonthEnd.slice(0, 8)}01`, lastMonthEnd];
    }
  }
};

const PRESETS: Preset[] = ['today', '7d', '30d', 'thisMonth', 'lastMonth'];

const Usage: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('summary');
  const [range, setRange] = useState<[string, string]>(() => presetRange('7d'));
  const [eventFilters, setEventFilters] = useState<UsageEventFilters>({});
  const [from, to] = range;
  const period = useMemo(() => kstPeriod(from, to), [from, to]);

  const openEvents = (filters: UsageEventFilters) => {
    setEventFilters(filters);
    setTab('events');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('usage.title')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('usage.subtitle')}</p>
        </div>
        {tab !== 'prices' && (
          <div className="flex flex-wrap items-center gap-2" data-testid="usage-period">
            <div className="flex gap-1" role="group" aria-label={t('usage.period')}>
              {PRESETS.map((p) => {
                const [pf, pt] = presetRange(p);
                const active = pf === from && pt === to;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setRange([pf, pt])}
                    aria-pressed={active}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      active
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {t(`usage.presets.${p}`)}
                  </button>
                );
              })}
            </div>
            <input
              type="date"
              className="input py-1.5 text-sm w-auto"
              value={from}
              max={to}
              onChange={(e) => e.target.value && setRange([e.target.value, to])}
              aria-label={t('usage.from')}
            />
            <span className="text-slate-400">~</span>
            <input
              type="date"
              className="input py-1.5 text-sm w-auto"
              value={to}
              min={from}
              onChange={(e) => e.target.value && setRange([from, e.target.value])}
              aria-label={t('usage.to')}
            />
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {(['summary', 'users', 'events', 'prices'] as Tab[]).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {t(`usage.tabs.${id}`)}
          </button>
        ))}
      </div>

      {tab === 'summary' && <SummaryPanel period={period} from={from} to={to} onOpenEvents={openEvents} />}
      {tab === 'users' && <UsersPanel period={period} from={from} to={to} onOpenEvents={openEvents} />}
      {tab === 'events' && <EventsPanel period={period} filters={eventFilters} onFiltersChange={setEventFilters} />}
      {tab === 'prices' && <PricesPanel />}
    </div>
  );
};

export default Usage;
